import React from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import SEO, { buildServiceJsonLd, buildBreadcrumbJsonLd } from "@/components/SEO";
import { PageHeader, Section } from "@/components/Primitives";
import QuoteForm from "@/components/QuoteForm";
import { SERVICES, CITIES, scSlug } from "@/data/config";
import { ArrowUpRight, Check } from "lucide-react";

const HERO_IMG = {
  "gutter-installation":
    "https://images.unsplash.com/photo-1498847559558-1e4b1a7f7a2f?crop=entropy&cs=srgb&fm=jpg&w=1400",
  "gutter-repair":
    "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?crop=entropy&cs=srgb&fm=jpg&w=1400",
  "gutter-cleaning":
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&w=1400",
  "gutter-guards":
    "https://images.unsplash.com/photo-1524230572899-a752b3835840?crop=entropy&cs=srgb&fm=jpg&w=1400",
  roofing:
    "https://images.unsplash.com/photo-1602193289141-9605ad75d0a5?crop=entropy&cs=srgb&fm=jpg&w=1400",
};

export default function Service() {
  const { pathname } = useLocation();
  const serviceSlug = pathname.replace(/^\//, "").replace(/\/$/, "");
  const service = SERVICES.find((s) => s.slug === serviceSlug);
  if (!service) return <Navigate to="/" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      buildServiceJsonLd(service),
      buildBreadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: service.name, path: `/${service.slug}` },
      ]),
    ],
  };

  return (
    <>
      <SEO
        title={service.title}
        description={service.meta}
        path={`/${service.slug}`}
        image={HERO_IMG[service.slug]}
        jsonLd={jsonLd}
      />
      <PageHeader
        overline={`Service ${service.accent}`}
        title={`${service.name} across Spokane and the Inland Northwest.`}
        lede={service.intro}
        image={HERO_IMG[service.slug]}
        alt={`${service.name} by Spokane Gutter Co.`}
      />

      <Section testid="service-detail">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <h2 className="overline mb-6">Scope of work</h2>
            <ul className="space-y-4">
              {service.bullets.map((b, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 border-b border-white/5 pb-4"
                >
                  <Check className="w-5 h-5 text-copper-500 mt-1 shrink-0" />
                  <span className="text-bone-100 text-lg leading-relaxed">{b}</span>
                </motion.li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-6 md:pl-10">
            <h2 className="overline mb-6">How the job goes</h2>
            <ol className="space-y-8">
              {service.process.map((p, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="grid grid-cols-[auto_1fr] gap-6 items-start"
                >
                  <span className="font-display text-4xl md:text-5xl text-copper-500 leading-none">
                    0{i + 1}
                  </span>
                  <span className="text-bone-100 text-lg leading-relaxed">{p}</span>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </Section>

      <Section testid="service-cities" className="bg-ink-900 border-y border-white/10">
        <p className="overline mb-4">Available in every city we serve</p>
        <h2 className="font-display text-3xl md:text-5xl tracking-tightest mb-10">
          {service.name} in every city we cover.
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {CITIES.map((c) => (
            <Link
              key={c.slug}
              to={`/${scSlug(c.slug, service.slug)}`}
              data-testid={`svc-city-link-${c.slug}`}
              className="group bg-ink-950 hover:bg-ink-900 p-6 md:p-8 flex justify-between items-center transition-colors duration-200"
            >
              <span className="font-display text-2xl md:text-3xl tracking-tightest text-bone-50 group-hover:text-copper-500">
                {c.name} {service.name}
              </span>
              <ArrowUpRight className="w-5 h-5 text-bone-400 group-hover:text-copper-500 group-hover:rotate-45 transition-transform" />
            </Link>
          ))}
        </div>
      </Section>

      {/* Cross-link to other services */}
      <Section testid="service-others">
        <p className="overline mb-4">Other services</p>
        <h2 className="font-display text-3xl md:text-5xl tracking-tightest mb-10">
          More of what we do in Spokane.
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {SERVICES.filter((sv) => sv.slug !== service.slug).map((sv) => (
            <Link
              key={sv.slug}
              to={`/${sv.slug}`}
              className="group bg-ink-950 hover:bg-ink-900 p-5 md:p-6 flex flex-col justify-between min-h-[140px]"
            >
              <span className="font-display text-2xl text-copper-500 leading-none">{sv.accent}</span>
              <span className="font-heading text-sm md:text-base text-bone-50 group-hover:text-copper-500 mt-6">
                {sv.name}
              </span>
            </Link>
          ))}
        </div>
      </Section>

      <Section testid="service-quote">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="overline mb-4">Free quote</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tightest">
              Book {service.name.toLowerCase()} at your house.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <QuoteForm preselectedService={service.name} />
          </div>
        </div>
      </Section>
    </>
  );
}
